/**
 * Roster import — admin-only modal that ingests HR's Excel file, computes
 * a smart diff against the live roster, and applies it after explicit
 * review.
 *
 * The expected file is iCons HR's "Team_progetti_persone" workbook. We
 * read its "People" sheet: Name / Surname / eMail / Unit 2026 / Unit 2026
 * secondment / Role 2026 (a few preamble rows above the header are fine).
 *
 * IDENTITY MATCHING is handled by utils/rosterMatch (email → exact name →
 * strict fuzzy), so a different spelling is recognised as the SAME person
 * rather than a phantom new hire. See that module for the full rationale.
 *
 * NO PRIMARY-KEY RENAMES. The displayed name comes from first_name /
 * last_name (see names.ts), not the roster `name` primary key. We correct
 * spellings by updating those non-PK fields — the UI shows the new name
 * immediately, the stable PK keeps every calendar row attached, nothing
 * cascades across the live database. New hires are inserted; leavers are
 * flagged former; existing people are PATCHed in place.
 *
 * BLOCKING SAFETY. The import refuses to run while there are unresolved
 * conflicts (an email matching an unrelated person) or email collisions
 * (a write that would violate roster.email UNIQUE) — these would
 * otherwise corrupt data or abort the batch half-written.
 *
 * All writes go through the raw-fetch helpers (restUpsert / restPatch /
 * restGet), which bypass the Supabase SDK auth-token lock. The apply path
 * is not a single transaction, so it pre-flights the session and reports
 * honestly if it fails partway (every write is idempotent → re-running is
 * safe).
 */
import { useState } from "preact/hooks";
import type { JSX } from "preact";
import * as XLSX from "xlsx";
import { state } from "../state/store";
import { toast } from "../utils/toast";
import { displayName, bySurname } from "../utils/names";
import { restUpsert, restPatch, restGet } from "../utils/withRetry";
import { ALLOWED_EMAIL_DOMAIN } from "../config";
import {
  buildRosterPlan,
  findEmailIssues,
  effectiveNewHireEmail,
  type ExcelPerson,
  type DbPerson,
  type RosterPlan,
  type PlannedUpdate,
  type EmailIssue,
} from "../utils/rosterMatch";
import type { RosterMap } from "../types";

interface Props {
  onClose: () => void;
}

const PREFERRED_SHEET_NAMES = ["people", "personale", "roster", "team", "icons team"];

function looksLikeRosterSheet(rows: unknown[][]): boolean {
  for (let i = 0; i < Math.min(rows.length, 25); i++) {
    const row = rows[i];
    if (!row) continue;
    const lower = row.map((c) => String(c ?? "").trim().toLowerCase());
    if (lower.some((c) => c === "name") && lower.some((c) => c === "surname")) return true;
  }
  return false;
}

function pickRosterSheet(wb: XLSX.WorkBook): { name: string; rows: unknown[][] } | null {
  if (wb.SheetNames.length === 0) return null;
  const tryByName = (lcaseName: string): { name: string; rows: unknown[][] } | null => {
    const actual = wb.SheetNames.find((n) => n.toLowerCase().trim() === lcaseName);
    if (!actual) return null;
    const sh = wb.Sheets[actual];
    if (!sh) return null;
    const rs = XLSX.utils.sheet_to_json<unknown[]>(sh, { header: 1, defval: "" });
    return looksLikeRosterSheet(rs) ? { name: actual, rows: rs } : null;
  };
  for (const candidate of PREFERRED_SHEET_NAMES) {
    const hit = tryByName(candidate);
    if (hit) return hit;
  }
  for (const name of wb.SheetNames) {
    const sh = wb.Sheets[name];
    if (!sh) continue;
    const rs = XLSX.utils.sheet_to_json<unknown[]>(sh, { header: 1, defval: "" });
    if (looksLikeRosterSheet(rs)) return { name, rows: rs };
  }
  const first = wb.SheetNames[0]!;
  const sh = wb.Sheets[first];
  if (!sh) return null;
  return { name: first, rows: XLSX.utils.sheet_to_json<unknown[]>(sh, { header: 1, defval: "" }) };
}

/** Locate the email column. Anchored detection so a "Mailing list" or
 *  "Email personale" column can't shadow the authoritative "eMail" one:
 *  prefer an exact-ish header, fall back to a tight substring that
 *  excludes mailing-/address-style labels. */
function findEmailColumn(header: string[]): number {
  const exact = header.findIndex((c) => ["email", "e-mail", "e mail", "emai", "mail"].includes(c));
  if (exact >= 0) return exact;
  return header.findIndex(
    (c) => /\be-?mail\b/.test(c) && !c.includes("mailing") && !c.includes("address") && !c.includes("indirizzo"),
  );
}

function excelDisplay(p: ExcelPerson): string {
  return `${p.lastName} ${p.firstName}`.replace(/\s+/g, " ").trim();
}

function parseExcelPeople(rows: unknown[][]): ExcelPerson[] {
  let headerIdx = -1;
  for (let i = 0; i < Math.min(rows.length, 25); i++) {
    const row = rows[i];
    if (!row) continue;
    const lower = row.map((c) => String(c ?? "").trim().toLowerCase());
    if (lower.some((c) => c === "name") && lower.some((c) => c === "surname")) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) {
    throw new Error(
      "Couldn't find a header row. The sheet should have a row with 'Name' and 'Surname' columns within the first 25 rows.",
    );
  }
  const header = (rows[headerIdx] ?? []).map((c) => String(c ?? "").trim().toLowerCase());
  const colName = header.indexOf("name");
  const colSurname = header.indexOf("surname");
  const colEmail = findEmailColumn(header);
  const colUnit = header.findIndex((c) => c.startsWith("unit") && !c.includes("secondment"));
  const colSecondment = header.findIndex((c) => c.includes("secondment"));
  const colRole = header.findIndex((c) => c.startsWith("role"));
  if (colUnit < 0) throw new Error("Missing a 'Unit' column.");
  if (colRole < 0) throw new Error("Missing a 'Role' column.");

  const out: ExcelPerson[] = [];
  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row) continue;
    const first = String(row[colName] ?? "").trim().replace(/\s+/g, " ");
    const last = String(row[colSurname] ?? "").trim().replace(/\s+/g, " ");
    if (!first || !last) continue;
    if (first.includes("#REF") || last.includes("#REF")) continue; // Excel error sentinel
    const unit = String(row[colUnit] ?? "").trim();
    const secondment = colSecondment >= 0 ? String(row[colSecondment] ?? "").trim() : "";
    const role = String(row[colRole] ?? "").trim();
    const rawEmail = colEmail >= 0 ? String(row[colEmail] ?? "").trim().toLowerCase() : "";
    out.push({
      firstName: first,
      lastName: last,
      email: rawEmail.includes("@") ? rawEmail : "",
      unit: secondment ? `${unit} / ${secondment}` : unit,
      role,
    });
  }
  if (out.length === 0) throw new Error("The sheet has no data rows below the header.");
  return out;
}

function dbPeopleFromState(roster: RosterMap): DbPerson[] {
  return Object.entries(roster).map(([name, e]) => ({
    name,
    email: e.email ?? "",
    unit: e.unit ?? "",
    role: e.role ?? "",
    firstName: e.first_name ?? "",
    lastName: e.last_name ?? "",
    active: e.active !== false,
  }));
}

interface Analysis {
  plan: RosterPlan;
  emailIssues: EmailIssue[];
}

// Stable per-change keys for the selective-apply checkboxes. A change is
// "selected" (will be applied) unless its key is in the `deselected` set.
const hireKey = (h: ExcelPerson): string => {
  const full = `${h.firstName} ${h.lastName}`.replace(/\s+/g, " ").trim();
  return `new:${full}`;
};
const updKey = (u: PlannedUpdate): string => `upd:${u.db.name}`;
const leaveKey = (l: DbPerson): string => `leave:${l.name}`;

export function RosterImport({ onClose }: Props): JSX.Element {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [busy, setBusy] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [partial, setPartial] = useState(false);
  const [usedSheet, setUsedSheet] = useState<string | null>(null);
  // Keys of changes the admin has UN-ticked. Empty = accept everything
  // (the default). Apply only writes the still-selected changes.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  const isSel = (key: string): boolean => !deselected.has(key);
  const toggleKey = (key: string): void =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setManyKeys = (keys: string[], select: boolean): void =>
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (select) next.delete(k);
        else next.add(k);
      }
      return next;
    });

  const onFile = async (e: Event): Promise<void> => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    setParseError(null);
    setApplyError(null);
    setPartial(false);
    setDeselected(new Set()); // a fresh file starts with everything accepted
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const picked = pickRosterSheet(wb);
      if (!picked) throw new Error("The workbook has no sheets.");
      setUsedSheet(picked.name);
      const excelPeople = parseExcelPeople(picked.rows);
      const dbPeople = dbPeopleFromState(state.value.roster);
      const plan = buildRosterPlan(excelPeople, dbPeople, ALLOWED_EMAIL_DOMAIN);
      const emailIssues = findEmailIssues(plan, dbPeople, ALLOWED_EMAIL_DOMAIN);
      setAnalysis({ plan, emailIssues });
    } catch (err) {
      console.error("Roster import parse failed:", err);
      setParseError(err instanceof Error ? err.message : "Could not read the file.");
      setUsedSheet(null);
    }
    input.value = "";
  };

  const apply = async (): Promise<void> => {
    if (!analysis) return;
    const { plan } = analysis;
    // Apply only the still-selected changes (everything is selected by
    // default; the admin can untick any they don't want).
    const selHires = plan.newHires.filter((h) => isSel(hireKey(h)));
    const selUpdates = plan.updates.filter((u) => isSel(updKey(u)));
    const selLeavers = plan.leavers.filter((l) => isSel(leaveKey(l)));
    if (selHires.length === 0 && selUpdates.length === 0 && selLeavers.length === 0) return;
    const activeCount = Object.values(state.value.roster).filter((e) => e.active !== false).length;
    if (selLeavers.length > activeCount * 0.2) {
      const ok = confirm(
        `⚠ HEADS UP\n\nThis would mark ${selLeavers.length} people as FORMER (out of ${activeCount} currently active). That's unusually high — usually it means the file lost a sheet or got truncated.\n\nContinue anyway?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    setApplyError(null);
    setPartial(false);

    // Pre-flight: a cheap read warms / validates the session token BEFORE
    // any write. If the token is stale this throws here, with nothing
    // modified yet — so the "no changes written" message stays truthful.
    try {
      await restGet("roster", "select=name&limit=1");
    } catch (err) {
      setApplyError(
        `Couldn't reach the database (${err instanceof Error ? err.message : String(err)}). No changes were written — try again.`,
      );
      setBusy(false);
      return;
    }

    let wrote = false;
    const fail = async (stage: string, err: unknown): Promise<void> => {
      const msg = err instanceof Error ? err.message : String(err);
      setApplyError(`${stage}: ${msg}`);
      setPartial(wrote);
      // Reflect the true DB state regardless of how far we got.
      await rehydrateRoster().catch(() => undefined);
      setBusy(false);
    };

    // 1. Insert genuine new hires (email from the file, or derived).
    if (selHires.length > 0) {
      const rows = selHires.map((h) => ({
        name: `${h.firstName} ${h.lastName}`.replace(/\s+/g, " ").trim(),
        unit: h.unit,
        role: h.role,
        email: effectiveNewHireEmail(h, ALLOWED_EMAIL_DOMAIN),
        active: true,
        first_name: h.firstName,
        last_name: h.lastName,
        last_active_day: null as string | null,
      }));
      try {
        await restUpsert("roster", rows);
        wrote = true;
      } catch (err) {
        await fail(`Insert new hires (${rows.length})`, err);
        return;
      }
    }

    // 2. Update existing people IN PLACE — keyed on the stable PK name.
    //    Only non-PK fields are sent (unit / role / first_name /
    //    last_name / email when corrected); never the `name` PK.
    for (const u of selUpdates) {
      const body: Record<string, unknown> = {};
      for (const c of u.changes) {
        if (c.field === "unit") body.unit = u.excel.unit;
        else if (c.field === "role") body.role = u.excel.role;
        else if (c.field === "first_name") body.first_name = u.excel.firstName;
        else if (c.field === "last_name") body.last_name = u.excel.lastName;
        else if (c.field === "email") body.email = c.to;
        else if (c.field === "reactivate") {
          body.active = true;
          body.last_active_day = null;
        }
      }
      if (Object.keys(body).length === 0) continue;
      try {
        await restPatch("roster", `name=eq.${encodeURIComponent(u.db.name)}`, body);
        wrote = true;
      } catch (err) {
        await fail(`Update ${u.db.name}`, err);
        return;
      }
    }

    // 3. Mark genuine leavers former, with a soft-retention last_active_day.
    {
      const cal = state.value.calendar;
      const notes = state.value.notes;
      const mtg = state.value.meetings;
      const computeLastDay = (person: string): string => {
        let maxDay = "";
        const tap = (m: Record<string, unknown> | undefined): void => {
          if (!m) return;
          for (const d of Object.keys(m)) if (d > maxDay) maxDay = d;
        };
        tap(cal[person]);
        tap(notes[person]);
        tap(mtg[person]);
        return maxDay || new Date().toISOString().slice(0, 10);
      };
      for (const l of selLeavers) {
        try {
          await restPatch("roster", `name=eq.${encodeURIComponent(l.name)}`, {
            active: false,
            last_active_day: computeLastDay(l.name),
          });
          wrote = true;
        } catch (err) {
          await fail(`Mark former (${l.name})`, err);
          return;
        }
      }
    }

    try {
      await rehydrateRoster();
    } catch (err) {
      await fail("Re-fetch after import", err);
      return;
    }

    toast(
      `Roster updated: ${selHires.length} added · ${selUpdates.length} updated · ${selLeavers.length} former`,
    );
    onClose();
  };

  const blocking =
    analysis !== null &&
    (analysis.plan.conflicts.length > 0 || analysis.emailIssues.length > 0);

  const totalChanges = analysis
    ? analysis.plan.newHires.length + analysis.plan.updates.length + analysis.plan.leavers.length
    : 0;
  const selectedCount = analysis
    ? analysis.plan.newHires.filter((h) => isSel(hireKey(h))).length +
      analysis.plan.updates.filter((u) => isSel(updKey(u))).length +
      analysis.plan.leavers.filter((l) => isSel(leaveKey(l))).length
    : 0;

  return (
    <div class="modal-backdrop open" role="presentation" onClick={onClose}>
      <div
        class="modal modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="importTitle"
        tabindex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="importTitle">Import roster from Excel</h3>
        <div class="modal-intro">
          Drop the iCons HR Excel file. People are matched by email and name
          (so a different spelling is the same person, not a new hire), you
          review the diff, and nothing is written until you confirm.
        </div>

        {!analysis && (
          <div class="import-picker">
            <input
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFile}
            />
            <div class="hint">
              Expected columns on the <code>People</code> sheet:{" "}
              <code>Name</code>, <code>Surname</code>, <code>eMail</code>,
              {" "}<code>Unit 2026</code>, <code>Unit 2026 secondment</code>,
              {" "}<code>Role 2026</code>.
            </div>
            {parseError && <div class="import-error">{parseError}</div>}
          </div>
        )}

        {analysis && (
          <>
            {usedSheet && (
              <div class="import-meta">
                Reading sheet <code>{usedSheet}</code> ·{" "}
                {Object.keys(state.value.roster).length} rows currently in the
                database
              </div>
            )}
            <ImportPreview
              analysis={analysis}
              isSel={isSel}
              toggleKey={toggleKey}
              setManyKeys={setManyKeys}
            />
            {applyError && (
              <div class="import-error import-error-block">
                <strong>
                  {partial
                    ? "Import stopped partway — some changes were already written."
                    : "Import failed — the database was NOT modified."}
                </strong>
                <pre>{applyError}</pre>
                <div class="hint">
                  {partial
                    ? "Every write is idempotent, so re-running this import is safe and will reconcile the rest. The roster below has been refreshed to reflect what actually landed."
                    : "Common causes: a duplicate email (UNIQUE on roster.email) or RLS blocking the write (are you a super admin?). DevTools → Console has the full stack."}
                </div>
              </div>
            )}
          </>
        )}

        <div class="modal-actions">
          <button type="button" class="modal-secondary" onClick={onClose}>
            {analysis ? "Cancel" : "Close"}
          </button>
          {analysis && (
            <button type="button" class="modal-secondary" onClick={() => setAnalysis(null)}>
              Pick a different file
            </button>
          )}
          {analysis && (
            <button
              type="button"
              class="modal-primary"
              disabled={busy || selectedCount === 0 || blocking}
              title={blocking ? "Resolve the blocking issues in the file first" : undefined}
              onClick={() => void apply()}
            >
              {busy
                ? "Applying…"
                : blocking
                  ? "Resolve issues first"
                  : selectedCount === 0
                    ? "Nothing selected"
                    : selectedCount === totalChanges
                      ? `Apply all ${totalChanges} change${totalChanges === 1 ? "" : "s"}`
                      : `Apply ${selectedCount} selected`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

async function rehydrateRoster(): Promise<void> {
  const rows = await restGet<{
    name: string; unit: string | null; role: string | null; email: string | null;
    active: boolean | null; first_name: string | null; last_name: string | null;
    last_active_day: string | null;
  }>("roster", "select=name,unit,role,email,active,first_name,last_name,last_active_day");
  const rosterMap: RosterMap = {};
  rows.forEach((r) => {
    rosterMap[r.name] = {
      unit: r.unit ?? "",
      role: r.role ?? "",
      email: r.email ?? "",
      active: r.active !== false,
      first_name: r.first_name ?? "",
      last_name: r.last_name ?? "",
      last_active_day: r.last_active_day ?? null,
    };
  });
  state.value = { ...state.value, roster: rosterMap };
}

function ImportPreview({
  analysis,
  isSel,
  toggleKey,
  setManyKeys,
}: {
  analysis: Analysis;
  isSel: (key: string) => boolean;
  toggleKey: (key: string) => void;
  setManyKeys: (keys: string[], select: boolean) => void;
}): JSX.Element {
  const { plan, emailIssues } = analysis;
  const nameCorrections = plan.updates.filter((u) => u.nameChanged);
  const fieldOnly = plan.updates.filter((u) => !u.nameChanged);
  const total = plan.newHires.length + plan.updates.length + plan.leavers.length;
  const hasBlocking = plan.conflicts.length > 0 || emailIssues.length > 0;

  // "Select all / none" control for a section, given its change keys.
  const SelectAll = ({ keys }: { keys: string[] }): JSX.Element => {
    const allSel = keys.every(isSel);
    return (
      <label class="import-selectall">
        <input
          type="checkbox"
          checked={allSel}
          onChange={() => setManyKeys(keys, !allSel)}
        />
        {allSel ? "Deselect all" : "Select all"}
      </label>
    );
  };

  if (total === 0 && !hasBlocking) {
    return (
      <div class="import-noop">
        ✓ No changes — the live roster already matches the file
        {plan.unchangedCount > 0 ? ` (${plan.unchangedCount} people checked)` : ""}.
      </div>
    );
  }

  return (
    <div class="import-preview">
      {hasBlocking && (
        <div class="import-error import-error-block">
          <strong>⛔ Resolve these in the Excel file before importing.</strong>
          {emailIssues.length > 0 && (
            <ul class="import-list">
              {emailIssues.map((iss, i) => (
                <li key={`e${i}`}>
                  <b>{iss.excel.firstName} {iss.excel.lastName}</b> — email{" "}
                  <code>{iss.email}</code> collides with {iss.collidesWith}.
                </li>
              ))}
            </ul>
          )}
          {plan.conflicts.length > 0 && (
            <ul class="import-list">
              {plan.conflicts.map((c, i) => (
                <li key={`c${i}`}>
                  <b>{c.excel.firstName} {c.excel.lastName}</b> — {c.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div class="import-summary">
        <span class="badge add">+{plan.newHires.length} new hires</span>
        <span class="badge namecorr">✎ {nameCorrections.length} name corrections</span>
        <span class="badge update">~{fieldOnly.length} field updates</span>
        <span class="badge former">−{plan.leavers.length} mark former</span>
        <span class="badge unchanged">✓ {plan.unchangedCount} unchanged</span>
        {plan.duplicateExcelRows.length > 0 && (
          <span class="badge unchanged">⎘ {plan.duplicateExcelRows.length} duplicate rows ignored</span>
        )}
      </div>

      {plan.newHires.length > 0 && (
        <details open>
          <summary>🟢 New hires ({plan.newHires.length})</summary>
          <div class="hint">Matched no existing person by email or name — added as new active employees.</div>
          <SelectAll keys={plan.newHires.map(hireKey)} />
          <ul class="import-list">
            {plan.newHires
              .slice()
              .sort((a, b) => a.lastName.localeCompare(b.lastName))
              .map((h) => (
                <li key={h.email || excelDisplay(h)}>
                  <label class="import-sel">
                    <input type="checkbox" checked={isSel(hireKey(h))} onChange={() => toggleKey(hireKey(h))} />
                    <span class="import-sel-body">
                      <b>{excelDisplay(h)}</b> — {h.unit || "—"} · {h.role || "—"}
                      <span class="email-derived">{effectiveNewHireEmail(h, ALLOWED_EMAIL_DOMAIN)}</span>
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        </details>
      )}

      {nameCorrections.length > 0 && (
        <details open>
          <summary>✎ Name corrections ({nameCorrections.length})</summary>
          <div class="hint">
            Recognised as the <strong>same person</strong> (matched by email
            or close name), with a spelling difference. Calendar history
            stays attached; only the displayed name (and any unit/role) is
            updated.
          </div>
          <SelectAll keys={nameCorrections.map(updKey)} />
          <ul class="import-list">
            {nameCorrections
              .slice()
              .sort((a, b) => bySurname(a.db.name, b.db.name))
              .map((u) => (
                <li key={u.db.name}>
                  <label class="import-sel">
                    <input type="checkbox" checked={isSel(updKey(u))} onChange={() => toggleKey(updKey(u))} />
                    <span class="import-sel-body">
                      <span class="namecorr-from">{displayName(u.db.name)}</span>
                      <span class="namecorr-arrow"> → </span>
                      <b class="namecorr-to">{excelDisplay(u.excel)}</b>
                      <span class={`match-method match-${u.method}`}>{u.method}</span>
                      <ChangeBullets u={u} skipName={false} />
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        </details>
      )}

      {fieldOnly.length > 0 && (
        <details open>
          <summary>🔵 Field updates ({fieldOnly.length})</summary>
          <SelectAll keys={fieldOnly.map(updKey)} />
          <ul class="import-list">
            {fieldOnly
              .slice()
              .sort((a, b) => bySurname(a.db.name, b.db.name))
              .map((u) => (
                <li key={u.db.name}>
                  <label class="import-sel">
                    <input type="checkbox" checked={isSel(updKey(u))} onChange={() => toggleKey(updKey(u))} />
                    <span class="import-sel-body">
                      <b>{displayName(u.db.name)}</b>
                      <ChangeBullets u={u} skipName={true} />
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        </details>
      )}

      {plan.leavers.length > 0 && (
        <details open>
          <summary>🔴 Will be marked as former ({plan.leavers.length})</summary>
          <div class="hint">
            Active in the database but absent from the file (by both email
            and name). Historical calendar data is preserved; they just stop
            appearing in active views.
          </div>
          <SelectAll keys={plan.leavers.map(leaveKey)} />
          <ul class="import-list">
            {plan.leavers
              .slice()
              .sort((a, b) => bySurname(a.name, b.name))
              .map((l) => (
                <li key={l.name}>
                  <label class="import-sel">
                    <input type="checkbox" checked={isSel(leaveKey(l))} onChange={() => toggleKey(leaveKey(l))} />
                    <span class="import-sel-body">
                      <b>{displayName(l.name)}</b>
                      <span class="email-derived">{l.email}</span>
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function ChangeBullets({ u, skipName }: { u: PlannedUpdate; skipName: boolean }): JSX.Element {
  const rows = u.changes.filter((c) => {
    if (skipName && (c.field === "first_name" || c.field === "last_name")) return false;
    return true;
  });
  if (rows.length === 0) return <span />;
  return (
    <ul class="import-changes">
      {rows.map((c, i) => (
        <li key={i}>
          {c.field === "reactivate"
            ? "was former → now active"
            : `${c.field.replace("_", " ")}: "${c.from}" → "${c.to}"`}
        </li>
      ))}
    </ul>
  );
}
